import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSettingsSharp } from 'react-icons/io5';
import ThresholdModal from 'common/components/thresholdModal/ThresholdModal';
import useWidgetSettings from '../../hooks/useWidgetSettings';
import styles from './MeterPercentSettings.module.scss';

// "Close to the target" percentages for km / usage hours, set per widget and kept in the browser.
// Used by "Lembretes a vencer em breve" and "Manutenções previstas" (backend: MeterProximityHelper).
export type MeterPercentSettings = { kmPercent: number; hoursPercent: number };

export type MeterPercentConfig = {
	widgetId: string;
	// Widget description, interpolated with {{kmPercent}} / {{hoursPercent}}
	descriptionKey: string;
	// Explanation shown in the settings modal
	settingsDescriptionKey: string;
};

// Same default as the backend
const DEFAULT_SETTINGS: MeterPercentSettings = { kmPercent: 10, hoursPercent: 10 };

export const useMeterPercentSettings = (config: MeterPercentConfig) => {
	const { t } = useTranslation();
	const [settings, setSettings] = useWidgetSettings<MeterPercentSettings>(config.widgetId, DEFAULT_SETTINGS);
	const description = t(config.descriptionKey as any, settings) as string;
	return { settings, setSettings, description };
};

type TriggerProps = {
	config: MeterPercentConfig;
	settings: MeterPercentSettings;
	onChange: (next: MeterPercentSettings) => void;
	// icon: widget header; link: picker drawer
	variant: 'icon' | 'link';
};

export const MeterPercentSettingsTrigger = ({ config, settings, onChange, variant }: TriggerProps) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const title = t('dashboard.meter_percent_settings.title' as any) as string;

	return (
		// Keep clicks (modal included: it is a portal, React still bubbles through here) away from the
		// parents: the picker card adds the widget on click
		<span className={styles.wrapper} onClick={e => e.stopPropagation()}>
			{variant === 'icon'
				? <button type="button" className={styles.iconButton} title={title} onClick={() => setIsOpen(true)}>
					<IoSettingsSharp />
				</button>
				: <button type="button" className={styles.link} onClick={() => setIsOpen(true)}>
					<IoSettingsSharp />
					{title}
				</button>}
			{isOpen && <ThresholdModal
				fields={[
					{ key: 'kmPercent', label: t('dashboard.meter_percent_settings.km_percent' as any) as string, unit: '%', value: settings.kmPercent, min: 0.01, max: 100, decimals: 2 },
					{ key: 'hoursPercent', label: t('dashboard.meter_percent_settings.hours_percent' as any) as string, unit: '%', value: settings.hoursPercent, min: 0.01, max: 100, decimals: 2 },
				]}
				description={t(config.settingsDescriptionKey as any) as string}
				onApply={values => { onChange({ kmPercent: values.kmPercent, hoursPercent: values.hoursPercent }); setIsOpen(false); }}
				onClose={() => setIsOpen(false)}
			/>}
		</span>
	);
};

// Picker drawer description: current percentages + a link to change them without opening the widget
export const meterPercentPickerDescription = (config: MeterPercentConfig) => {
	const MeterPercentPickerDescription = ({ className }: { className: string }) => {
		const { settings, setSettings, description } = useMeterPercentSettings(config);
		return (
			<p className={className}>
				{description}{' '}
				<MeterPercentSettingsTrigger config={config} settings={settings} onChange={setSettings} variant="link" />
			</p>
		);
	};
	return MeterPercentPickerDescription;
};
